import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedin, FaTwitter, FaInstagram, FaYoutube, FaFacebook, FaDownload } from 'react-icons/fa';
import { useSocialLinks } from '../context/SocialLinksContext';
import { usePortfolio } from '../context/PortfolioContext';
import AutoTyping from './AutoTyping';

const Hero = () => {
  const { socialLinks } = useSocialLinks();
  const { portfolioData } = usePortfolio();
  const profile = portfolioData?.profile || {};
  const roles = Array.isArray(profile.roles) && profile.roles.length > 0 ? profile.roles : ['AI Engineer', 'Freelancer', 'YouTuber', 'Innovator'];
  const [imageError, setImageError] = useState(false);

  const handleImageError = (e) => {
    setImageError(true);
    e.target.style.display = 'none';
  };

  return (
    <section id="home" className="min-h-screen flex items-center py-20 bg-gray-900">
      <div className="container">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <motion.div 
            className="w-full md:w-1/2"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Hi, I'm <span className="text-primary">{profile.name || 'Dhiraj T H'}</span>
            </h1>
            <div className="text-2xl md:text-3xl font-medium text-white h-12">
              <span>I'm a </span>
              <AutoTyping roles={profile.roles} />
            </div>
            {profile.location && (
              <div className="text-gray-400 mt-2">{profile.location}</div>
            )}
            {profile.email && (
              <div className="text-gray-400 mt-1">Email: <a href={`mailto:${profile.email}`} className="underline text-primary">{profile.email}</a></div>
            )}
            {profile.phone && (
              <div className="text-gray-400 mt-1">Phone: <a href={`tel:${profile.phone}`} className="underline text-primary">{profile.phone}</a></div>
            )}
            {profile.cv && (
              <div className="mt-4">
                <a href={profile.cv} target="_blank" rel="noopener noreferrer" className="btn btn-primary flex items-center gap-2">
                  <FaDownload /> Download CV
                </a>
              </div>
            )}
            {profile.tagline && (
              <p className="text-gray-300 my-6 text-lg md:max-w-xl">{profile.tagline}</p>
            )}
            {(profile.show_button && profile.button_text && profile.button_link) || true ? (
              <div className="flex flex-wrap items-center gap-4 mt-6">
                {/* View My Work button - always first */}
                <a
                  href="#work"
                  className="btn border-2 border-primary text-primary hover:bg-primary/10"
                >
                  View My Work
                </a>
                {/* Dynamic button from AdminDashboard */}
                {profile.show_button && profile.button_text && profile.button_link && (
                  <a
                    href={profile.button_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn border-2 border-primary text-primary hover:bg-primary/10"
                  >
                    {profile.button_text}
                  </a>
                )}
              </div>
            ) : null}
            <div className="flex gap-6 mt-10">
              {socialLinks?.github?.visible && socialLinks.github.links[0] && (
                <a 
                  href={socialLinks.github.links[0]} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  <FaGithub size={24} />
                </a>
              )}
              {socialLinks?.linkedin?.visible && socialLinks.linkedin.links[0] && (
                <a 
                  href={socialLinks.linkedin.links[0]} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  <FaLinkedin size={24} />
                </a>
              )}
              {socialLinks?.twitter?.visible && socialLinks.twitter.links[0] && (
                <a 
                  href={socialLinks.twitter.links[0]} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  <FaTwitter size={24} />
                </a>
              )}
              {socialLinks?.instagram?.visible && socialLinks.instagram.links[0] && (
                <a 
                  href={socialLinks.instagram.links[0]} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  <FaInstagram size={24} />
                </a>
              )}
              {socialLinks?.youtube?.visible && socialLinks.youtube.links[0] && (
                <a 
                  href={socialLinks.youtube.links[0]} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  <FaYoutube size={24} />
                </a>
              )}
              {socialLinks?.facebook?.visible && socialLinks.facebook.links[0] && (
                <a 
                  href={socialLinks.facebook.links[0]} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  <FaFacebook size={24} />
                </a>
              )}
            </div>

          </motion.div>
          <motion.div 
            className="w-full md:w-1/2 flex justify-center"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative w-full max-w-md">
              {!imageError && (
                <>
                  <div className="absolute -inset-0.5 bg-primary rounded-full blur-xl opacity-75 animate-pulse"></div>
                  <img 
                    src={profile.image_url || '/profile-image.png'}
                    alt={profile.name || 'Profile'}
                    className="relative w-full rounded-full border-4 border-secondary"
                    onError={handleImageError}
                  />
                </>
              )}
              {imageError && (
                <div className="w-full aspect-square rounded-full border-4 border-secondary bg-gray-800 flex items-center justify-center">
                  <span className="text-4xl text-primary">DT</span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 